// The server refused a request until this person accepts a legal document (451). One blocking dialog, whatever
// number of requests came back refused: the Blazor LegalAcceptanceDialog.
//
// Not dismissible on purpose: every request behind it comes back 451, so a notice the person could close would leave
// them clicking through an app where nothing works and nothing says why. The two ways on are accepting and signing out.

import { cabinetApi } from './cabinet-api.ts';
import { h } from './dom.ts';
import { t } from './i18n.ts';
import { log } from './logs.ts';
import { renderMarkdown } from './markdown.ts';
import { on, state } from './state.ts';

/** One version of a legal document, as legal-documents/version/{id} answers it. */
interface LegalVersion {
    documentId: string;
    versionId: string;
    versionNumber?: number;
    publishedAt?: string;
    changeSummary?: string | null;
    type?: string;
    sourceLocale?: string;
    bodies?: Array<{ locale: string; markdown?: string }>;
}

let open = false;

const typeLabel = (type: string | undefined): string => t(`influencer.web.legal.documentType.${type}`) || (type ?? '');

const localesOf = (document: LegalVersion): string[] => (document.bodies || []).map(body => body.locale);

/**
 * The body shown - the same rule the server uses to decide which one this caller WOULD be shown, because it keeps the
 * locale reported here and compares it with the request's on every later call; a mismatch brings the dialog back.
 */
function shownLocale(document: LegalVersion, chosen: Record<string, string>): string | undefined {
    if (chosen[document.documentId]) return chosen[document.documentId];

    const locales = localesOf(document);
    const selected = state.locale || document.sourceLocale || 'en';
    const language = selected.split('-')[0]!.toLowerCase();

    return locales.find(locale => locale.toLowerCase() === selected.toLowerCase())
        ?? locales.find(locale => locale.split('-')[0]!.toLowerCase() === language)
        ?? locales.find(locale => locale.toLowerCase() === (document.sourceLocale || '').toLowerCase())
        ?? locales[0]
        ?? document.sourceLocale;
}

/** required is what the 451 named. */
function showDialog(required: Array<{ versionId: string }>, actions: { onSignOut: () => Promise<void> }): void {
    open = true;

    const s: {
        loading: boolean;
        loadError: boolean;
        documents: LegalVersion[];
        active: number;
        accepting: boolean;
        chosen: Record<string, string>;
    } = { loading: true, loadError: false, documents: [], active: 0, accepting: false, chosen: {} };
    const content = h('div', { class: 'legal-content' });
    const dialog = h('div', { class: 'dialog-backdrop', role: 'presentation' },
        h('div', { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'legalTitle' },
            h('div', { class: 'dialog-title', id: 'legalTitle' }, t('influencer.web.legal.acceptance.title')),
            content,
            h('div', { class: 'dialog-actions' },
                h('button', { type: 'button', class: 'btn text-button', onclick: () => signOut() },
                    t('influencer.web.legal.acceptance.signOutButton')))));

    // Fetched by version id from the 451 body, never through legal-documents/required: that endpoint and the guard
    // both derive a scope from the request, and if they ever disagreed the app would be refused forever while being
    // told it owes nothing. A version id resolves no scope at all.
    async function load(): Promise<void> {
        s.loading = true;
        s.loadError = false;
        draw();

        const loaded: LegalVersion[] = [];

        for (const requirement of required) {
            const response = await cabinetApi(`legal-documents/version/${requirement.versionId}`);
            const document = response.ok ? response.json<LegalVersion>() : null;

            // One failure fails the screen: showing three of four documents would let somebody accept all on offer
            // and stay locked out.
            if (!document || typeof document !== 'object') {
                log.error(`Could not load legal document version ${requirement.versionId}`, undefined, undefined,
                    'Lify.Influencer.Services.Realization.LegalDocumentService');
                s.loadError = true;
                s.loading = false;
                draw();

                return;
            }

            loaded.push(document);
        }

        s.documents = loaded;
        s.loading = false;
        draw();
    }

    async function accept(document: LegalVersion): Promise<void> {
        s.accepting = true;
        draw();

        // The locale actually on screen: the evidence of what was read, and the server refuses one the version lacks.
        const response = await cabinetApi('legal-documents/accept', {
            method: 'POST',
            json: { DocumentId: document.documentId, VersionId: document.versionId, ShownLocale: shownLocale(document, s.chosen) }
        });

        s.accepting = false;

        if (!response.ok) {
            log.error(`Could not record acceptance of version ${document.versionId}`, undefined, undefined,
                'Lify.Influencer.Services.Realization.LegalDocumentService');
            s.loadError = true;
            draw();

            return;
        }

        s.documents = s.documents.filter(d => d !== document);
        s.active = 0;

        if (s.documents.length) {
            draw();

            return;
        }

        close();
        location.reload();
    }

    async function signOut(): Promise<void> {
        close();
        await actions.onSignOut();
    }

    function close(): void {
        dialog.remove();
        open = false;
        document.removeEventListener('keydown', trapEscape, true);
    }

    function trapEscape(event: KeyboardEvent): void {
        if (event.key === 'Escape') event.preventDefault();
    }

    function drawDocument(document: LegalVersion): HTMLElement {
        const locales = localesOf(document);
        const locale = shownLocale(document, s.chosen);
        const body = (document.bodies || []).find(b => (b.locale || '').toLowerCase() === (locale || '').toLowerCase());
        const published = document.publishedAt ? String(document.publishedAt).slice(0, 10) : '';

        return h('div', { class: 'legal-panel' },
            h('div', { class: 'legal-caption' }, `v${document.versionNumber} · ${published}`),
            document.changeSummary && document.changeSummary.trim()
                ? h('div', { class: 'legal-alert info' }, `${t('influencer.web.legal.acceptance.whatChanged')}: ${document.changeSummary}`)
                : null,
            locales.length > 1
                ? h('label', { class: 'legal-language' },
                    h('span', null, t('influencer.web.legal.acceptance.languageLabel')),
                    h('select', {
                        onchange: (event: Event) => {
                            s.chosen[document.documentId] = (event.target as HTMLSelectElement).value;
                            draw();
                        }
                    }, locales.map(l => h('option', { value: l, selected: l === locale }, l))))
                : null,
            h('div', { class: 'legal-body' }, renderMarkdown(body?.markdown || '')),
            h('button', { type: 'button', class: 'btn accept-button', disabled: s.accepting, onclick: () => accept(document) },
                s.accepting ? h('span', { class: 'spinner small' }) : null,
                t('influencer.web.legal.acceptance.acceptButton')));
    }

    function draw(): void {
        if (s.loading) {
            content.replaceChildren(h('div', { class: 'progress-linear' }));
        } else if (s.loadError) {
            // There is no way out but signing out, so the reason stays on screen.
            content.replaceChildren(
                h('div', { class: 'legal-alert error' }, t('influencer.web.legal.acceptance.loadError')),
                h('button', { type: 'button', class: 'btn accept-button', onclick: () => load() }, t('influencer.web.legal.acceptance.retryButton')));
        } else if (!s.documents.length) {
            content.replaceChildren(h('div', { class: 'legal-alert success' }, t('influencer.web.legal.acceptance.allAccepted')));
        } else {
            content.replaceChildren(
                h('p', { class: 'legal-intro' }, t('influencer.web.legal.acceptance.intro')),
                h('div', { class: 'legal-tabs', role: 'tablist' }, s.documents.map((document, index) => h('button', {
                    type: 'button',
                    role: 'tab',
                    class: 'legal-tab' + (index === s.active ? ' active' : ''),
                    'aria-selected': index === s.active ? 'true' : 'false',
                    onclick: () => {
                        s.active = index;
                        draw();
                    }
                }, typeLabel(document.type)))),
                drawDocument(s.documents[Math.min(s.active, s.documents.length - 1)]!));
        }
    }

    document.body.appendChild(dialog);
    document.addEventListener('keydown', trapEscape, true);
    void load();
}

export function initLegalGate(actions: { onSignOut: () => Promise<void> }): void {
    on('legalBlocked', event => {
        // A 451 arrives on every request in flight at once; one dialog, not one per request.
        if (!open) showDialog((event as CustomEvent<Array<{ versionId: string }>>).detail, actions);
    });
}
